import { intersection } from 'lodash-es'
import { useContext, useRef, useState } from 'react'
import Immutable from 'immutable'

import { ColumnContext, contactAndMapsPlugins } from './MultiColumnBlock'
import { ImageUploadControl } from '../../../UnsplashImageSearch/components/ImageUploadControl'
import { Popover } from '../../../Popover'
import { allowedMimeTypes } from '../../Workspace/mimeTypes'
import { getPluginTypes } from './MultiColumnBlock.helpers'
import { useTranslate } from '../../../../utils/translate'

export function ActionBar({ index, data, element, onDataChange, onEditButtonClick, onChangeImage, columnRef }) {
  const t = useTranslate('interface', 'components.pluginContextBarComponent')

  const [isDeletion, setIsDeletion] = useState(false)
  const [isImageToolbarOpen, setIsImageToolbarOpen] = useState(false)

  const columnContext = useContext(ColumnContext)
  const [editorMode, editAction] = columnContext.columnModeState[0][index] || []
  const isInEditMode = editorMode === 'edit' && editAction === null

  const fileInputRef = useRef<HTMLInputElement>(null)

  // single element, no hover menu
  if (isDeletion) {
    return (
      <div className="dali-grid-column-actionbar">
        <DeleteConfirmButton
          data={data}
          index={index}
          onDataChange={(data) => {
            setIsDeletion(false)
            onDataChange(data)
          }}
          onCancel={() => setIsDeletion(false)}
        />
      </div>
    )
  }

  const columnsCount = data.get('columns').size
  const isStart = index === 0
  const isEnd = index === columnsCount - 1
  const isOnlyItem = columnsCount === 1

  const isLastRemainingContactOrMapsItem =
    intersection(getPluginTypes(data.get('columns')), contactAndMapsPlugins).length === 1 &&
    intersection(getPluginTypes(Immutable.List([element])), contactAndMapsPlugins).length > 0

  const isTextPlugin = element?.get('type') === 'epages.text'
  const isImagePlugin = ['epages.image', 'epages.heroimage'].includes(element?.get('type'))
  const isPlaceholder = !element.getIn(['data', 'src'])
  const isSingleEditButton = isOnlyItem && !isTextPlugin

  return (
    <div className="dali-grid-column-actionbar">
      {!isOnlyItem && (
        <>
          <MoveColumnLeftButton data={data} index={index} onDataChange={onDataChange} disabled={isStart} />
          <MoveColumnRightButton data={data} index={index} onDataChange={onDataChange} disabled={isEnd} />
          <div className="dali-grid-column-actionbar-button-separator"></div>
        </>
      )}

      {isImagePlugin && !isPlaceholder && (
        <Popover
          open={isImageToolbarOpen || isInEditMode}
          onOpenChange={setIsImageToolbarOpen}
          side="top"
          align="center"
          sideOffset={30}
          hideArrow
          usePortal
          className="dali-actionbar-image-toolbar-popover"
          virtualRef={columnRef}
          trigger={
            <button
              type="button"
              className="dali-grid-column-actionbar-button dali-grid-column-actionbar-button-edit"
              title={t('editElementButton.label')}
            >
              <span className={isSingleEditButton ? '' : 'visually-hidden'}>{t('editElementButton.label')}</span>
            </button>
          }
        >
          <div className="dali-actionbar-image-toolbar">
            <ImageUploadControl
              onUnsplashSelect={onChangeImage}
              onUploadClick={() => fileInputRef.current?.click()}
              buttonClassName="dali-actionbar-image-toolbar-button-change"
              buttonContent={<span>{t('changeImageButton.label')}</span>}
              buttonTooltip={t('changeImageButton.label')}
              side="top"
              imageSearchSideOffset={3}
              optionsPopoverSideOffset={8}
            />
            <input
              ref={fileInputRef}
              type="file"
              hidden
              accept={allowedMimeTypes.join(',')}
              onChange={async (event) => {
                const file = event.target.files?.[0]
                if (file) {
                  onChangeImage(file)

                  // Unset the value to force that the "change" event is triggered
                  // even if the same file is selected again.
                  event.target.value = ''
                }
              }}
            />
            <button
              type="button"
              className="dali-actionbar-image-toolbar-button-crop"
              title={t('editImageFormatButton.label')}
              onClick={() => {
                onEditButtonClick('editImage')
              }}
            >
              <span className="visually-hidden">{t('editImageFormatButton.label')}</span>
            </button>
            <button
              type="button"
              className={
                isInEditMode
                  ? 'dali-actionbar-image-toolbar-button-text active'
                  : 'dali-actionbar-image-toolbar-button-text'
              }
              title={t('editImageTextButton.label')}
              onClick={() => {
                onEditButtonClick(null)
              }}
            >
              <span className="visually-hidden">{t('editImageTextButton.label')}</span>
            </button>
          </div>
        </Popover>
      )}

      {!isTextPlugin && !isImagePlugin && (
        <button
          type="button"
          className="dali-grid-column-actionbar-button  dali-grid-column-actionbar-button-edit"
          title={t('editElementButton.label')}
          onClick={() => onEditButtonClick(null)}
        >
          <span className={isSingleEditButton ? '' : 'visually-hidden'}>{t('editElementButton.label')}</span>
        </button>
      )}

      {!isOnlyItem && !isLastRemainingContactOrMapsItem && (
        <button
          className="dali-grid-column-actionbar-button dali-grid-column-actionbar-button-delete"
          onClick={() => setIsDeletion(true)}
          title={t('deleteButton.label')}
        >
          <span className="visually-hidden">{t('deleteButton.label')}</span>
        </button>
      )}
    </div>
  )
}

function MoveColumnLeftButton({ data, index, onDataChange, disabled }) {
  const t = useTranslate('interface', 'components.pluginContextBarComponent')

  return (
    <button
      className="dali-grid-column-actionbar-button dali-grid-column-actionbar-button-left"
      onClick={() => onDataChange(moveColumn(data, index, 'left'))}
      title={t('moveLeftButton.label')}
      disabled={disabled}
    >
      <span className="visually-hidden">{t('moveLeftButton.label')}</span>
    </button>
  )
}

function MoveColumnRightButton({ data, index, onDataChange, disabled }) {
  const t = useTranslate('interface', 'components.pluginContextBarComponent')

  return (
    <button
      className="dali-grid-column-actionbar-button dali-grid-column-actionbar-button-right"
      onClick={() => onDataChange(moveColumn(data, index, 'right'))}
      title={t('moveRightButton.label')}
      disabled={disabled}
    >
      <span className="visually-hidden">{t('moveRightButton.label')}</span>
    </button>
  )
}

function DeleteConfirmButton({ data, index, onDataChange, onCancel }) {
  const t = useTranslate('interface', 'components.pluginContextBarComponent')

  return (
    <>
      <span className="dali-grid-column-actionbar-delete-warning">{t('deleteButton.warningMessage')}</span>
      <div className="dali-grid-column-actionbar-button-separator"></div>
      <button
        className="dali-grid-column-actionbar-button dali-grid-column-actionbar-button-delete-confirm"
        onClick={() => onDataChange(data.removeIn(['columns', index]))}
      >
        {t('deleteConfirmButton.label')}
      </button>
      <div className="dali-grid-column-actionbar-button-separator"></div>
      <button
        className="dali-grid-column-actionbar-button dali-grid-column-actionbar-button-delete-cancel"
        onClick={onCancel}
      >
        {t('deleteDismissButton.label')}
      </button>
    </>
  )
}

function moveColumn(data: ImmutableMap, index: number, direction: 'left' | 'right') {
  if (index === 0 && direction === 'left') return data
  if (index === data.get('columns').size - 1 && direction === 'right') return data

  const columns = data.get('columns')
  const newIndex = direction === 'left' ? index - 1 : index + 1

  // make sure settings layers follow their anchor in 100% of cases (see EPUI-3132)
  // floating-ui's autoUpdate's layoutShift detection somehow won't recognize column / block moves,
  // but it reliably reruns on scroll events
  setTimeout(() => {
    document.getElementById('body-wrapper')?.dispatchEvent(new Event('scroll'))
  }, 0)

  return data.setIn(['columns', index], columns.get(newIndex)).setIn(['columns', newIndex], columns.get(index))
}
